import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Hook that triggers a callback when user clicks outside the referenced element
 * Works with React Native Web components and portals like react-datepicker
 * @param ref - React ref object pointing to the element to detect outside clicks for
 * @param handler - Callback function to execute when outside click is detected
 */
const useOnClickOutside = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: RefObject<any>,
  handler: (event: MouseEvent | TouchEvent) => void,
  portalId: string = 'datepickers-portal',
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref?.current;
      if (!el) return;

      // For React Native Web, access the underlying DOM node
      const domNode = el._touchableNode || el;

      // Grab the datepicker portal (or any custom popper container)
      const portalEl = document.getElementById(portalId);

      // Do nothing if clicking inside the ref element or its children
      if (domNode && domNode.contains && domNode.contains(event.target)) {
        return;
      }

      // Do nothing if clicking inside the portal (datepicker popper)
      if (portalEl && portalEl.contains(event.target as Node)) {
        return;
      }

      // Otherwise it's an outside click
      handler(event);
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      }
    };
  }, [ref, handler, portalId]);
};

export default useOnClickOutside;
